import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Target, TrendingUp, Flame, AlertTriangle, Mail, MessageCircle, Calendar,
  Users, DollarSign, FileText, Handshake, Star, Activity, Loader2,
} from "lucide-react";

const CARDS = [
  { key: "high_intent_opportunities",      label: "High-Intent Opportunities", href: "/growth/opportunities?status=discovered&min_priority=0.7", icon: Flame,          tone: "amber" },
  { key: "qualified_leads",                label: "Qualified Leads",           href: "/growth/leads?status=qualified",                            icon: Users,          tone: "indigo" },
  { key: "outreach_ready_for_review",      label: "Outreach for Review",       href: "/growth/outreach?status=awaiting_approval",                 icon: Mail,           tone: "violet" },
  { key: "conversations_needing_response", label: "Conversations Needing Reply", href: "/growth/outreach?status=replied",                         icon: MessageCircle,  tone: "sky" },
  { key: "follow_ups_due",                 label: "Follow-Ups Due",            href: "/growth/leads?followups=due",                               icon: Calendar,       tone: "rose" },
  { key: "trials_at_risk",                 label: "Trials at Risk",            href: "/growth/activation?bucket=at_risk",                         icon: AlertTriangle,  tone: "amber" },
  { key: "new_users_yesterday",            label: "New Users Yesterday",       href: "/growth/analytics",                                          icon: TrendingUp,     tone: "emerald" },
  { key: "new_paid_companies",             label: "New Paid Companies",        href: "/growth/analytics",                                          icon: DollarSign,     tone: "emerald" },
  { key: "content_waiting_publish",        label: "Content Awaiting Publish",  href: "/growth/content",                                            icon: FileText,       tone: "indigo" },
  { key: "partnership_replies",            label: "Partnership Replies",       href: "/growth/partnerships",                                       icon: Handshake,      tone: "violet" },
  { key: "testimonial_opportunities",      label: "Testimonial Opportunities", href: "/growth/testimonials",                                       icon: Star,           tone: "amber" },
  { key: "agent_activity_needing_attention", label: "AI Activity Needing Attention", href: "/growth/agent-log?result=warn",                       icon: Activity,       tone: "rose" },
];

const toneRing = {
  amber: "ring-amber-500/30 hover:ring-amber-400", indigo: "ring-indigo-500/30 hover:ring-indigo-400",
  violet: "ring-violet-500/30 hover:ring-violet-400", sky: "ring-sky-500/30 hover:ring-sky-400",
  rose: "ring-rose-500/30 hover:ring-rose-400", emerald: "ring-emerald-500/30 hover:ring-emerald-400",
};
const toneText = {
  amber: "text-amber-300", indigo: "text-indigo-300", violet: "text-violet-300",
  sky: "text-sky-300", rose: "text-rose-300", emerald: "text-emerald-300",
};

export default function GrowthCommandCenter() {
  const [goal, setGoal] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [g, s] = await Promise.all([api.get("/growth/goal"), api.get("/growth/summary")]);
        setGoal(g.data);
        setSummary(s.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-10 text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading command center…</div>;

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto" data-testid="growth-command-center">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Command Center</h1>
        <p className="text-slate-400 mt-1 text-sm">1,000 users by January 1, 2027. Every lane, every opportunity, every conversation.</p>
      </div>

      {/* Goal bar */}
      {goal && (
        <Card className="bg-gradient-to-br from-indigo-500/10 via-violet-500/10 to-slate-900 border border-indigo-500/30" data-testid="goal-card">
          <CardContent className="p-6 md:p-7 space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-widest text-indigo-300 flex items-center gap-2"><Target className="h-3.5 w-3.5" /> Goal</p>
                <p className="mt-2 text-4xl md:text-5xl font-extrabold text-white tracking-tight">
                  {goal.users_total.toLocaleString()}<span className="text-slate-500"> / {goal.goal.toLocaleString()}</span>
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  {goal.remaining > 0 ? <>{goal.remaining.toLocaleString()} to go</> : <span className="text-emerald-400 font-bold">Goal reached — keep going</span>}
                  <span className="text-slate-600 mx-2">·</span>
                  {goal.days_left} days left
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Stat label="Users / day needed" value={goal.needed_per_day} />
                <Stat label="7-day actual rate" value={`${goal.run_rate_7d}/day`} />
                <Stat label="30-day actual rate" value={`${goal.run_rate_30d}/day`} />
                <Stat label="At pace by Jan 1" value={goal.projected_total_at_pace.toLocaleString()} />
              </div>
            </div>
            {/* Progress bar with milestone pins */}
            <div className="relative h-3 rounded-full bg-slate-800 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500"
                style={{ width: `${Math.min(100, goal.percent_complete)}%` }}
                data-testid="goal-progress"
              />
              {goal.milestones.map((m) => (
                <div
                  key={m}
                  className="absolute top-0 bottom-0 w-px bg-slate-500/60"
                  style={{ left: `${(m / goal.goal) * 100}%` }}
                  title={`${m.toLocaleString()}`}
                />
              ))}
            </div>
            <div className="flex items-center justify-between text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              <span>0</span><span>250</span><span>500</span><span>750</span><span>1,000</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 12 alert cards */}
      <div>
        <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-400 mb-3">Needs your attention</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {CARDS.map(({ key, label, href, icon: Icon, tone }) => {
            const n = summary?.counts?.[key] ?? 0;
            return (
              <Link key={key} to={href} data-testid={`card-${key}`}>
                <Card className={`bg-slate-900/60 border-slate-800 ring-1 transition-all ${toneRing[tone]} cursor-pointer`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <Icon className={`h-5 w-5 ${toneText[tone]}`} />
                      <p className="text-3xl font-extrabold text-white tracking-tight">{n}</p>
                    </div>
                    <p className="text-xs text-slate-400 mt-2 leading-tight">{label}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex gap-3 flex-wrap">
        <Link to="/growth/opportunities">
          <Button className="bg-indigo-600 hover:bg-indigo-500" data-testid="btn-open-radar">Open Opportunity Radar</Button>
        </Link>
        <Link to="/growth/settings">
          <Button variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white" data-testid="btn-open-settings">Growth Settings</Button>
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">{label}</p>
      <p className="text-base font-extrabold text-white mt-0.5">{value}</p>
    </div>
  );
}
